import {
  collection,
  doc,
  serverTimestamp,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import type {
  FirestoreUserProfileCustomProduct,
  FirestoreUserProfileCustomProductWrite,
} from './schema'
import { schema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { StreamInterface } from 'tricklejs/dist/types'
import {
  modelListStream,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import { UserProfileCustomProduct } from '../../models/UserProfileCustomProduct'
import {
  deleteDocWithError,
  setDocWithError,
  getDocsWithError,
} from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserProfileCustomProduct> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing custom product', e.errors)
      }
      throw e
    }
  },
}

const getColRef = (
  firestore: Firestore,
  userId: string
): CollectionReference<FirestoreUserProfileCustomProduct, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'custom_product'
  ).withConverter(converter)
}

export const getUserProfileCustomProducts = (
  repository: FirebaseRepository,
  params: { userId: string }
): StreamInterface<UserProfileCustomProduct[]> => {
  const ref = getColRef(repository.firestore, params.userId)

  return modelListStream(repository, ref, UserProfileCustomProduct)
}

export const fetchUserProfileCustomProducts = async (
  repository: FirebaseRepository,
  params: { userId: string }
): Promise<UserProfileCustomProduct[]> => {
  const ref = getColRef(repository.firestore, params.userId)
  const docs = await getDocsWithError(
    ref,
    'FetchUserProfileCustomProductsError'
  )

  return docs.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, UserProfileCustomProduct)
  )
}

export const createUserProfileCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    productName: string
    sectionPassQuantity: number
    sectionPassPrice: number
    expiresAt?: Date
    productType: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, params.userId)
  const docRef = doc(ref)

  const data: FirestoreUserProfileCustomProductWrite = {
    productId: docRef.id,
    userId: params.userId,
    creatorUserId: repository.uid,
    productName: params.productName,
    productType: params.productType,
    sectionPassQuantity: params.sectionPassQuantity,
    sectionPassPrice: params.sectionPassPrice,
    expiresAt: params.expiresAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  await setDocWithError(docRef, data)
  return docRef.id
}

export const updateUserProfileCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    productId: string
    productName?: string
    sectionPassQuantity?: number
    sectionPassPrice?: number
    expiresAt?: Date
    productType?: string
  }
): Promise<void> => {
  const { userId, productId, ...rest } = params
  const ref = getColRef(repository.firestore, userId)
  const docRef = doc(ref, productId)

  const updateData: Partial<FirestoreUserProfileCustomProductWrite> = {
    ...rest,
    creatorUserId: repository.uid,
    updatedAt: serverTimestamp(),
  }

  await setDocWithError(docRef, updateData, { merge: true })
}

export const removeUserProfileCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    productId: string
  }
): Promise<void> => {
  const ref = doc(
    getColRef(repository.firestore, params.userId),
    params.productId
  )

  return deleteDocWithError(ref)
}
